import React, { useEffect, useState } from "react";
import { useMessageSource } from "react-message-source";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import FormFullWidthContainer from "../../ui/layout/page/forms-container/form-full-width-container";
import TypographyContainer from "../../ui/layout/page/typography-container/typography-base-container";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import FlowHeader from "../../ui/widgets/flow-header";
import CustomLink from "../../ui/widgets/link";
import { createApplicationPdfLink } from "../../util/pdf";
import { CardholderModel } from "../cardholder-basic-information/cardholder-form/types";
import ProductImage from "../shared/components/product-image";
import { usePageTitle } from "../shared/hooks/usePageTitle";
import CustomTypography from "../../ui/widgets/typography";
import { ProductRefType } from "../../context/product-context/types";
import WarningBox from "../../ui/widgets/warning-box";

export default function Summary({
  flowType,
  context,
  previous,
  next,
}: {
  flowType: ApplicationFlows;
  previous: () => void;
  next: () => void;
  context: {
    companyName: string;
    cardholder: CardholderModel;
    basicApplicationRef: string;
    cardApplicationRef: string;
    selectedProduct: ProductRefType;
  };
}): React.JSX.Element {
  const { getMessage, getMessageWithNamedParams } = useMessageSource();
  const [basicApplicationPdfLink, setBasicApplicationPdfLink] = useState<
    string | null
  >(null);
  const [cardApplicationPdfLink, setCardApplicationPdfLink] = useState<
    string | null
  >(null);

  const {
    companyName,
    cardholder: { firstName, lastName },
    selectedProduct,
    basicApplicationRef,
    cardApplicationRef,
  } = context;

  useEffect(() => {
    const run = async () => {
      const basicAccountPdfLink =
        await createApplicationPdfLink(basicApplicationRef);
      setBasicApplicationPdfLink(basicAccountPdfLink);

      const cardApplicationPdfLink =
        await createApplicationPdfLink(cardApplicationRef);
      setCardApplicationPdfLink(cardApplicationPdfLink);
    };
    run();
  }, [basicApplicationRef, cardApplicationRef]);

  const isNotInAdditionalCardholderFlow =
    flowType !== ApplicationFlows.ADDITIONAL_CA;

  return (
    <>
      <FlowHeader
        title={usePageTitle(flowType)}
        image={<ProductImage product={selectedProduct} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <TypographyContainer>
          <ContentHeader title={getMessage("Summary.summary-title")} />
        </TypographyContainer>
        <WarningBox
          header={getMessage("Sign.complete-process-warning.title")}
          text={getMessage("Sign.complete-process-warning.text")}
          imgAlt={getMessage("Sign.complete-process-warning.imgAlt")}
          isInfo={true}
        />
        <FormFullWidthContainer hasAdditionalTopMargin={true}>
          <TypographyItemContainer>
            {isNotInAdditionalCardholderFlow && (
              <CustomLink
                href={basicApplicationPdfLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getMessageWithNamedParams(
                  "CardholderConfirmation.print-out-as-master-account",
                  { companyName },
                )}
              </CustomLink>
            )}
          </TypographyItemContainer>
          <TypographyItemContainer>
            <CustomLink
              href={cardApplicationPdfLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getMessageWithNamedParams(
                "CardholderConfirmation.print-out-as-card-application",
                {
                  firstName,
                  lastName,
                },
              )}
            </CustomLink>
          </TypographyItemContainer>
        </FormFullWidthContainer>
        <ContentFooter>
          <div className={"scd-section-pusher-lg"}>
            <TypographyItemContainer>
              <CustomTypography variant={"h4"}>
                {getMessage("Summary.information.title")}
              </CustomTypography>
            </TypographyItemContainer>
          </div>
          <NavigationBar
            onBack={previous}
            onNext={next}
            backButtonText={getMessage(
              "Summary.information-is-not-correct-button",
            )}
            nextButtonText={getMessage("Summary.information-is-correct-button")}
          />
        </ContentFooter>
      </ApplicationPageContent>
    </>
  );
}
